import type { CompanyDiscoveryAiOutput } from '@revelio/data-access';

import { getFilterDefinition } from '../../engine/filterRegistry';
import type { Filter, TreeFilter } from '../../types/filters';
import {
  type AllSelectionListsWithSkillTaxonomyKey,
  FilterId,
  FilterType,
} from '../../types/filters';
import { transformEntityInfo } from './transformEntityInfo';
import { transformPrimarySelector } from './transformPrimarySelector';
import { transformSegments } from './transformSegments';

export type ScreenerAiTransformUnknownFilter = {
  name: string;
  relevantPromptText?: string;
  explanation?: string;
};

/**
 * Transforms CompanyDiscoveryAiOutput (backend screener AI response) into CustomDash Filter[].
 * Combines primary selector, segments (with segmentation), and entity info selectors.
 */
export const transformScreenerAiResponseToCustomDashFilters = (
  response: CompanyDiscoveryAiOutput
): {
  filters: Filter[];
  unknownFilters: ScreenerAiTransformUnknownFilter[];
} => {
  const { filters: responseFilters } = response;
  const unknownFilters: ScreenerAiTransformUnknownFilter[] = (
    response.unknownFilters ?? []
  ).map((u) => ({
    name: u.name,
    relevantPromptText: u.relevantPromptText,
    explanation: u.explanation,
  }));

  const filters: Filter[] = [];

  const primary = transformPrimarySelector(responseFilters.primary_selector);
  if (primary) {
    filters.push(primary);
    if (primary.id === FilterId.COMPANY && primary.type === FilterType.TREE) {
      const industryDef = getFilterDefinition(FilterId.INDUSTRY);
      if (industryDef?.type === FilterType.TREE) {
        const selectionListIds =
          (industryDef as { selectionListIds?: string[] }).selectionListIds ??
          [];
        const treePrimary = primary as TreeFilter;
        const selectionsForIndustry = treePrimary.selections.map((s) => {
          const idStr = String(s.id);
          const normalizedId = idStr.includes('.')
            ? (idStr.split('.').pop() ?? idStr)
            : idStr;
          return { ...s, id: normalizedId };
        });
        const industryFilter: TreeFilter = {
          ...industryDef,
          id: FilterId.INDUSTRY,
          name: industryDef.name,
          type: FilterType.TREE,
          selectionListIds:
            selectionListIds as AllSelectionListsWithSkillTaxonomyKey[],
          selections: selectionsForIndustry,
          isPrimary: false,
          isUserSubmitted: true,
        };
        filters.push(industryFilter);
      }
    }
  }

  const segmentFilters = transformSegments(responseFilters.segments);
  filters.push(...segmentFilters);

  const companyInfoSelectors =
    responseFilters.entity_info_selectors?.company_info_selectors;
  const entityFilters = transformEntityInfo(companyInfoSelectors);
  filters.push(...entityFilters);

  return { filters, unknownFilters };
};
